// Section layout widths, shared by the sections editor (which row-packs them), the section editor
// panel (which offers them), and the content previews (which adapt to narrow columns).
//
// This lives in its own module rather than beside the packer in StorefrontSectionsEditor because
// StorefrontSectionsEditor -> StorefrontSectionEditor -> ContentSectionForms is already an import
// chain; importing back up from either of those would close a cycle.

/** Percent width per section `width` key. Absent/full = 100 (the section gets its own row). */
export const WIDTH_FRACTION: Record<string, number> = {
    quarter: 25,
    third: 100 / 3,
    half: 50,
    twoThirds: 200 / 3,
    threeQuarters: 75,
    full: 100,
};

/** Gap between packed sections in a row, in px. */
export const ROW_GAP = 16;

// Floating-point slack for width comparisons: (100/3) * 3 === 100.00000000000001 in IEEE 754, so an
// exact `> 100` row-break test would push three thirds onto two rows. One hundredth of a percent is
// far below any real width and comfortably above the accumulated error.
export const PACK_EPSILON = 0.01;

/** The percent width of a `width` value; unknown/absent values are full width. */
export const widthPctFor = (width?: string): number => WIDTH_FRACTION[width ?? 'full'] ?? 100;

/** Does this width put the section in a shared row (i.e. anything narrower than full)? */
export const isFractionalWidth = (width?: string): boolean => widthPctFor(width) < 100;

/**
 * Is this width narrow enough (a third or less) that horizontal content layouts have to stack?
 * twoThirds/threeQuarters stay wide enough for side-by-side content.
 */
export const isNarrowWidth = (width?: string): boolean => widthPctFor(width) <= 100 / 3 + PACK_EPSILON;

// 4-decimal rounding that drops trailing zeros (25, not 25.0000) — used to build the flex basis.
const round4 = (n: number): number => Number(n.toFixed(4));

/**
 * The flex basis a fractional column gets inside a packed row. The row's `gap` eats ROW_GAP px per
 * seam, so each column gives back its SHARE of that gap rather than a flat ROW_GAP — a quarter
 * gives back 12px (3/4 of the gap), a half 8px, a third 10.6667px. Matches the shopper's math
 * exactly, so preview column widths equal live column widths.
 */
export const columnFlexBasis = (widthPct: number): string => {
    const f = widthPct / 100;
    return `calc(${round4(widthPct)}% - ${round4(ROW_GAP * (1 - f))}px)`;
};
